import Button from 'components/base/Button';
import PageBreadcrumb from 'components/common/PageBreadcrumb';
import { EmployeeBreadcrumbItems } from 'data/commonData';
import { Col, Form, Row } from 'react-bootstrap';
import CreateEmployeeForm from 'components/forms/CreateStaffForm';
import { ChangeEvent, FormEvent, useState } from 'react';
import {
  CreateEmployeeDTO,
  CreateEmployeeFormData
} from 'data/from-data/create-employee-form.data';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { CREATE_EMPLOYEE, GET_EMPLOYEE_ROLES } from 'services/api/employee';
import { FormProps } from 'data/from-data/interface/form-props.interface';
import { useNavigate } from 'react-router-dom';
import { Link } from 'react-router-dom';
import { useAccessToken } from 'providers/AccessTokenProvider';
import { Role } from 'data/users';
import { toast } from 'react-hot-toast';
import { formatPhoneNumber } from './utils/helper';

const CreateEmployee = () => {
  const [validated, setValidated] = useState<boolean>(false);
  const [createEmployeeDto, setCreateEmployeeDto] =
    useState<CreateEmployeeFormData>(new CreateEmployeeFormData());
  const [isAdmin, setIsAdmin] = useState<boolean>(false);
  const [isFieldOperator, setIsFieldOperator] = useState<boolean>(false);
  const [isCallOutUser, setIsCallOutUser] = useState<boolean>(false);
  const [isLoading, setLoading] = useState<boolean>(false);
  const { authUser } = useAccessToken();
  const navigate = useNavigate();
  const queries = useQueryClient();

  const { data: rolesData, isLoading: rolesLoading } = useQuery<Role[]>(
    'getEmployeeRoles',
    GET_EMPLOYEE_ROLES
  );

  const createEmployeeAction = useMutation('createEmployee', CREATE_EMPLOYEE, {
    onError: (err: any) => {
      toast.error(err.response.data.messages || 'Error creating employee');
      setLoading(false);
    },
    onSuccess: () => {
      setLoading(false);
      toast.success('Employee created successfully');
      queries.invalidateQueries('get_employee');
      queries.invalidateQueries('getEmployees');
      navigate('/home/apps/employee');
    },
    onMutate: () => {
      setLoading(true);
    }
  });

  const handleSubmit = (event: FormEvent) => {
    const form = event.currentTarget as HTMLFormElement;

    if (form.checkValidity() === false) {
      event.preventDefault();
      event.stopPropagation();
    } else {
      return runFormSubmissionAction(event);
    }

    setValidated(true);
  };

  const handleInputChange = (
    e: ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;

    setCreateEmployeeDto(prevData => ({
      ...prevData,
      [name]: {
        ...prevData[name],
        value
      }
    }));
  };

  const runFormSubmissionAction = async (e: FormEvent) => {
    e.preventDefault();

    const {
      email: { value: email },
      firstName: { value: firstName },
      lastName: { value: lastName },
      username: { value: username },
      password: { value: password },
      phoneNumber: { value: phoneNumber },
      roles: { value: roles },
      isCallOutUser: { value: callOutUserValue }
    } = createEmployeeDto;

    const payload: CreateEmployeeDTO = {
      ...createEmployeeDto,
      email,
      firstName,
      lastName,
      username,
      password,
      phoneNumber: formatPhoneNumber(phoneNumber),
      roles: isAdmin ? [] : roles,
      verified: true,
      isAdmin: isAdmin,
      isFieldOperator: isFieldOperator,
      isCallOutUser: isCallOutUser || callOutUserValue === true
    };
    await createEmployeeAction.mutateAsync(payload);
  };

  const renderFormFields = () => {
    return Object.entries(createEmployeeDto)
      .filter(([key]) => key !== 'isCallOutUser')
      .map(([key, formProps]) => (
        <Col md="6" key={key}>
          <CreateEmployeeForm
            formProps={formProps as unknown as FormProps}
            handleInputChange={handleInputChange}
            availableRoles={rolesData || []}
            rolesLoading={rolesLoading}
            isAdmin={isAdmin}
          />
        </Col>
      ));
  };

  const handleAdminChange = (isChecked: boolean) => {
    setIsAdmin(isChecked);
    if (isChecked) {
      setIsFieldOperator(false);
    }
  };

  const handleFieldOperatorChange = (isChecked: boolean) => {
    setIsFieldOperator(isChecked);
    if (isChecked) {
      setIsAdmin(false);
    }
  };

  const handleCallOutUserChange = (isChecked: boolean) => {
    setIsCallOutUser(isChecked);
    setCreateEmployeeDto(prevData => ({
      ...prevData,
      isCallOutUser: {
        ...prevData.isCallOutUser,
        value: isChecked
      }
    }));
  };

  return (
    <div className="mb-9">
      <PageBreadcrumb items={EmployeeBreadcrumbItems} />
      <div className="border-bottom mb-7 mx-n3 px-2 mx-lg-n6 px-lg-6">
        <Row>
          <Col xl={9}>
            <div className="d-sm-flex justify-content-between">
              <h2 className="mb-4">Create Employee</h2>
              <div className="d-flex mb-3">
                <Link to="/home/apps/employee">
                  <Button variant="phoenix-primary" className="me-2 px-6">
                    Cancel
                  </Button>
                </Link>
              </div>
            </div>
          </Col>
        </Row>
      </div>
      <Row>
        <Col xl={9}>
          <Form noValidate validated={validated} onSubmit={handleSubmit}>
            <Row className="g-3 mb-3">{renderFormFields()}</Row>
            {authUser?.isAdmin && (
              <div className="d-flex mb-3 gap-5">
                <Form.Group className="position-relative">
                  <Form.Check
                    label="Make this employee an admin"
                    name="isAdmin"
                    onChange={e => {
                      handleAdminChange(e.target.checked);
                    }}
                    checked={isAdmin}
                  />
                </Form.Group>
                <Form.Group className="position-relative">
                  <Form.Check
                    label="Make this employee a field operator"
                    name="isFieldOperator"
                    onChange={e => {
                      handleFieldOperatorChange(e.target.checked);
                    }}
                    checked={isFieldOperator}
                  />
                </Form.Group>
                <Form.Group className="position-relative">
                  <Form.Check
                    label="Make this employee a Call-Out Cal user"
                    name="isCallOutUser"
                    onChange={e => {
                      handleCallOutUserChange(e.target.checked);
                    }}
                    checked={isCallOutUser}
                  />
                </Form.Group>
              </div>
            )}
            <div className="text-end">
              <Button
                variant="primary"
                className=""
                type="submit"
                disabled={isLoading}
              >
                {isLoading ? 'Creating...' : 'Create Employee'}
              </Button>
            </div>
          </Form>
        </Col>
      </Row>
    </div>
  );
};

export default CreateEmployee;
